import { canAccessModule } from "./licenseHelpers.mjs";

export const DEFAULT_ROLE_KEYS = ["vacation-default", "crm-default", "ai-default", "recruiter-default", "useradmin-default"];
export const HOME_DEFAULT_MODULE_KEY = "home-default-module";

const HOME_DEFAULT_TARGETS = Object.freeze({
  crm: "/ncrm",
  ai: "/ai-workspace",
  admin: "/admin/workflows",
});
const HR_DASHBOARD_ROLE_KEYS = new Set(["admin", "miniadmin", "vacation-hr", "hr"]);

export const normalizeRoleKey = (role) => (role ? String(role).trim().toLowerCase() : null);

export const expandRoleAliases = (roleKey) => {
  const key = normalizeRoleKey(roleKey);
  if (!key) return [];
  const aliases = [key];
  if (key === "admin" || key === "miniadmin") {
    return aliases;
  }
  if (key.startsWith("vacation-")) {
    if (key === "vacation-hr") aliases.push("hr");
    if (key === "vacation-user" || key === "vacation-default") aliases.push("user");
    if (key === "vacation-manager") aliases.push("manager");
    if (key === "vacation-director") aliases.push("director");
    return aliases;
  }
  if (key.startsWith("crm-")) {
    aliases.push("crm");
    if (key === "crm-admin") aliases.push("crm-operator");
    if (key === "crm-caller") aliases.push("caller");
    return aliases;
  }
  if (key.startsWith("recruiter-")) {
    // Permite acces pe rutele care așteaptă roluri de tip "crm" sau "user" în logica veche
    aliases.push("crm", "user");
    return aliases;
  }
  if (key.startsWith("useradmin-")) {
    aliases.push("admin");
    return aliases;
  }
  return aliases;
};

const buildNormalizedRoleSet = (roleKeys = [], legacyRole = null) => {
  const set = new Set();
  const sourceRoles = [];

  if (Array.isArray(roleKeys)) {
    sourceRoles.push(...roleKeys);
  } else if (roleKeys instanceof Set) {
    sourceRoles.push(...Array.from(roleKeys));
  } else if (roleKeys) {
    sourceRoles.push(roleKeys);
  }

  if (legacyRole) {
    sourceRoles.push(legacyRole);
  }

  sourceRoles.forEach((role) => {
    const normalized = normalizeRoleKey(role);
    if (!normalized) return;
    set.add(normalized);
    expandRoleAliases(normalized).forEach((alias) => set.add(alias));
  });

  return set;
};

export const resolveDefaultModuleRoute = (moduleId, roleKeys = [], legacyRole = null, licenseSnapshot = null) => {
  const normalizedModuleId = normalizeRoleKey(moduleId);
  if (normalizedModuleId === "vacation") {
    if (
      !canAccessModule("hr-core", {
        roleKeys,
        licenseSnapshot,
        fallbackIfUnknown: true,
      })
    ) {
      return null;
    }
    const roleSet = buildNormalizedRoleSet(roleKeys, legacyRole);
    const shouldOpenHrDashboard = Array.from(HR_DASHBOARD_ROLE_KEYS).some((role) => roleSet.has(role));
    return shouldOpenHrDashboard ? "/hr-dashboard" : "/dashboard";
  }
  if (
    normalizedModuleId === "crm" &&
    !canAccessModule("crm", {
      roleKeys,
      licenseSnapshot,
      fallbackIfUnknown: true,
    })
  ) {
    return null;
  }
  return HOME_DEFAULT_TARGETS[normalizedModuleId] || null;
};

export const resolveRedirectPath = (roleKeys = [], legacyRole = null, options = {}) => {
  const normalizedLegacy = normalizeRoleKey(legacyRole);
  const normalizedRoles = (roleKeys || []).map((r) => normalizeRoleKey(r)).filter(Boolean);
  const licenseSnapshot = options?.licenseSnapshot || null;
  const preferredRoute = resolveDefaultModuleRoute(
    options?.preferredModuleId,
    normalizedRoles,
    normalizedLegacy,
    licenseSnapshot
  );
  if (preferredRoute) {
    return preferredRoute;
  }

  const nonDefaults = normalizedRoles.filter((r) => !DEFAULT_ROLE_KEYS.includes(r));
  // Priorizăm rolurile non-default, în ordinea de mai jos
  const priority = [
    "admin",
    "miniadmin",
    "vacation-hr",
    "useradmin-default",
    "vacation-manager",
    "vacation-director",
    "vacation-user",
    "crm-admin",
    "crm-operator",
    "crm-caller",
    "privacy-manager",
    "ai-default",
    "miridar",
    "crm-default",
    "recruiter-hr",
    "recruiter-default",
    "recruiter-viewer",
    "vacation-default",
  ];

  const resolveRouteForRole = (roleKey) => {
    if (roleKey === "admin" || roleKey === "miniadmin") return { path: "/", moduleKey: null };
    if (roleKey === "vacation-hr") return { path: "/hr-dashboard", moduleKey: "hr-core" };
    if (["vacation-manager", "vacation-director", "vacation-user", "vacation-default"].includes(roleKey)) {
      return { path: "/dashboard", moduleKey: "hr-core" };
    }
    if (["crm-admin", "crm-operator", "crm-caller"].includes(roleKey)) {
      return { path: "/ncrm", moduleKey: "crm" };
    }
    if (roleKey === "privacy-manager") {
      return { path: "/ncrm/data-integrations", moduleKey: "crm" };
    }
    if (roleKey === "ai-default") {
      return { path: "/ai-workspace", moduleKey: null };
    }
    if (roleKey === "miridar") {
      return { path: "/ncrm/data-integrations", moduleKey: "crm" };
    }
    if (roleKey === "crm-default") {
      // Rol legacy din vechiul /Crm (eliminat). Noul CRM (/ncrm) NU îl suportă la backend
      // (ACTIVITY_ROLES/CONTACTS_READ_ROLES/DEAL_LOOKUP_ROLES nu includ crm-default), deci
      // l-ar trimite la un board care răspunde 403. Aterizare sigură pe home până la migrarea
      // utilizatorilor către crm-operator/crm-caller.
      return { path: "/", moduleKey: null };
    }
    if (["recruiter-default", "recruiter-hr", "recruiter-viewer"].includes(roleKey)) {
      return { path: "/Candidati", moduleKey: null };
    }
    if (roleKey === "useradmin-default") {
      return { path: "/users", moduleKey: null };
    }
    return null;
  };

  const firstMatch = priority.find((roleKey) => {
    if (!nonDefaults.includes(roleKey)) return false;
    const target = resolveRouteForRole(roleKey);
    if (!target?.moduleKey) return true;
    return canAccessModule(target.moduleKey, {
      roleKeys: normalizedRoles,
      licenseSnapshot,
      fallbackIfUnknown: true,
    });
  });
  const defaults = normalizedRoles.filter((r) => DEFAULT_ROLE_KEYS.includes(r));

  if (firstMatch) {
    const target = resolveRouteForRole(firstMatch);
    if (target?.path) return target.path;
  }

  if (defaults.length === 1) {
    const onlyDefault = defaults[0];
    const target = resolveRouteForRole(onlyDefault);
    if (
      target?.path &&
      (!target.moduleKey ||
        canAccessModule(target.moduleKey, {
          roleKeys: normalizedRoles,
          licenseSnapshot,
          fallbackIfUnknown: true,
        }))
    ) {
      return target.path;
    }
  }

  if (
    normalizedLegacy === "caller" &&
    canAccessModule("crm", { roleKeys: normalizedRoles, licenseSnapshot, fallbackIfUnknown: true })
  ) {
    return "/ncrm";
  }
  if (
    ["user", "manager", "director"].includes(normalizedLegacy) &&
    canAccessModule("hr-core", { roleKeys: normalizedRoles, licenseSnapshot, fallbackIfUnknown: true })
  ) {
    return "/dashboard";
  }
  if (normalizedLegacy === "hr") return "/hr-dashboard";
  if (
    normalizedLegacy === "crm" &&
    canAccessModule("crm", { roleKeys: normalizedRoles, licenseSnapshot, fallbackIfUnknown: true })
  ) {
    return "/ncrm";
  }
  if (normalizedLegacy === "admin" || normalizedLegacy === "miniadmin") return "/";
  return "/";
};
